import { useMemo } from 'react';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import {
  DEFAULT_HEADLINE_TEXT,
  DEFAULT_SUBLINE_TEXT,
} from '@rover/react-lib/src/pages/index/HomePage/HomePage.constants';
import { selectors as homePageSelectors } from '@rover/react-lib/src/pages/index/HomePage/HomePage.duck';
import { useApiFrontendHomepageConfigurationRetrieve } from '@rover/rsdk/src/apiClient/latest/api-frontend-homepage-configuration-retrieve';
import type { HomepageConfiguration as ApiHomepageConfiguration } from '@rover/rsdk/src/apiClient/latest/model/homepageConfiguration';
import type {
  HomepageConfiguration as ReduxHomepageConfiguration,
  HomepageMetaTag,
  HomepageMetaTags,
} from '@rover/types/src/HomepageConfiguration';

import useDucks from './useDucks';

export type DefaultHomepageConfigurationSelectors = Omit<
  ReduxHomepageConfiguration,
  HomepageMetaTag
> & {
  metaTags: HomepageMetaTags;
};

export type ShimmedHomepageConfigurationSelectors = Omit<
  ApiHomepageConfiguration,
  HomepageMetaTag
> & {
  metaTags: HomepageMetaTags;
};

const selector = (state): DefaultHomepageConfigurationSelectors => ({
  metaTags: homePageSelectors.getMetaTags(state),
  headlineText: homePageSelectors.getHeadlineText(state),
  subHeadlineText: homePageSelectors.getSubheadlineText(state),
  locationName: homePageSelectors.getLocationName(state),
  hasActivePets: homePageSelectors.getHasActivePets(state),
});

// transforms the raw api data to match the current duck pattern
const transformReactQueryData = (
  reactQueryData: ApiHomepageConfiguration | undefined
): DefaultHomepageConfigurationSelectors => {
  const { title, metaDescription, ...data } = reactQueryData ?? {
    headlineText: DEFAULT_HEADLINE_TEXT(),
    subHeadlineText: DEFAULT_SUBLINE_TEXT(),
    locationName: null,
    hasActivePets: false,
    title: '',
    metaDescription: '',
  };

  const metaTags = { title, metaDescription };

  return { ...data, metaTags };
};

const useHomePageConfigurationShim = (): ShimmedHomepageConfigurationSelectors => {
  const { isReactQueryRoute } = usePageContext();
  const { data: selectedData } = useDucks({ selector });
  const { data: reactQueryData } = useApiFrontendHomepageConfigurationRetrieve({
    query: { enabled: isReactQueryRoute },
  });

  return useMemo(() => {
    const { headlineText, subHeadlineText, ...data } = isReactQueryRoute
      ? transformReactQueryData(reactQueryData)
      : selectedData;

    // headlineText and subHeadlineText only need to accept message descriptors because of the default text. The api will always return strings for these (or null for subHeadlineText). To ease typing down the line, this shim calls i18n on the defaults and returns the defaults as strings.

    // subHeadlineText needs to be nullable. Down the line, the existence of the subhead determines styling of the surrounding elements
    return {
      ...data,
      headlineText,
      subHeadlineText,
    };
  }, [isReactQueryRoute, reactQueryData, selectedData]);
};

export default useHomePageConfigurationShim;
